<template>
    <v-container fluid class="page-wrap pa-0 bg-background">

        <FullPageLoader :visible="isLoading" />

        <!-- HERO -->
        <section fluid class="hero-section py-8 text-center mb-4">
            <v-container class="hero-inner" max-width="1200">
                <h1 class="hero-title">
                    Book Your Adventure
                </h1>
            </v-container>
        </section>

        <!-- MAIN CONTENT -->
        <v-container class="content-inner py-10 px-6 px-md-10" max-width="1200">
            <div class="two-rail">
                <!-- LEFT: SAILING SUMMARY + SELECTED CABIN -->
                <aside class="left-panel">
                    <v-img :src="hero" alt="Cruise image" height="180" cover class="left-hero" />

                    <div class="left-content">
                        <!-- TOP SUMMARY -->
                        <div class="left-main">
                            <div class="left-title text-h6 font-weight-bold mb-3">
                                {{ title }}
                            </div>

                            <v-row dense class="mb-1">
                                <v-col cols="5" class="label">Leaving from</v-col>
                                <v-col cols="7" class="value">{{ departFrom }}</v-col>
                            </v-row>

                            <v-row dense class="mb-1">
                                <v-col cols="5" class="label">Onboard</v-col>
                                <v-col cols="7" class="value">{{ shipName }}</v-col>
                            </v-row>

                            <v-row dense class="mb-1">
                                <v-col cols="5" class="label">Dates</v-col>
                                <v-col cols="7" class="value d-flex align-center">
                                    <span>{{ startDateFmt }}</span>
                                    <v-icon size="18" class="mx-2">mdi-arrow-right</v-icon>
                                    <span>{{ endDateFmt }}</span>
                                </v-col>
                            </v-row>

                            <v-divider class="my-4" />

                            <div class="text-subtitle-1 font-weight-bold mb-2">
                                Room selection
                            </div>

                            <v-row dense>
                                <v-col cols="5" class="label">Guests</v-col>
                                <v-col cols="7" class="value">{{ guestsLabel }}</v-col>
                            </v-row>

                            <!-- SELECTED CABIN SUMMARY (non-price bits) -->
                            <template v-if="selectedListing">
                                <v-divider class="my-4" />

                                <div class="text-subtitle-1 font-weight-bold mb-2">
                                    Selected cabin
                                </div>

                                <v-row dense class="mb-1">
                                    <v-col cols="5" class="label">Type</v-col>
                                    <v-col cols="7" class="value">
                                        {{ selectedListing.room_type || selectedTab }}
                                    </v-col>
                                </v-row>

                                <v-row dense class="mb-1">
                                    <v-col cols="5" class="label">Cabin</v-col>
                                    <v-col cols="7" class="value">
                                        {{ selectedListing.cabin_number || 'TBD' }}
                                    </v-col>
                                </v-row>

                                <v-row dense class="mb-1">
                                    <v-col cols="5" class="label">Sleeps</v-col>
                                    <v-col cols="7" class="value">
                                        {{ selectedListing.guest_limit || 'TBD' }}
                                    </v-col>
                                </v-row>
                            </template>

                            <template v-else>
                                <v-divider class="my-4" />
                                <div class="text-caption text-grey-darken-1">
                                    Choose a cabin on the right to see its details here.
                                </div>
                            </template>
                        </div>

                        <!-- BOTTOM: PRICE SUMMARY -->
                        <div class="pricing-summary mt-4 pt-3" :class="{ 'pricing-section--empty': !selectedListing }">
                            <v-divider class="mb-4" />

                            <div class="text-subtitle-1 font-weight-bold mb-2">
                                Trip total
                            </div>

                            <template v-if="selectedListing">
                                <div class="pricing-row">
                                    <v-col cols="5" class="label">Total</v-col>
                                    <v-col cols="7" class="value">
                                        {{ currency(selectedListing.total_guest_price) }}
                                    </v-col>
                                </div>

                                <div class="pricing-row">
                                    <v-col cols="5" class="label">Deposit</v-col>
                                    <v-col cols="7" class="value">
                                        {{ currency(selectedListing.deposit) }}
                                    </v-col>
                                </div>

                                <div class="pricing-row" v-if="selectedListing.tips_included != null">
                                    <v-col cols="5" class="label">Tips included</v-col>
                                    <v-col cols="7" class="value">
                                        {{ selectedListing.tips_included }}
                                    </v-col>
                                </div>
                            </template>

                            <template v-else>
                                <div class="pricing-row">
                                    <span class="pricing-label text-grey-darken-1">
                                        Select a cabin to view pricing details.
                                    </span>
                                </div>
                            </template>
                        </div>
                    </div>
                </aside>


                <!-- RIGHT: CABIN OPTIONS -->
                <section class="right-rail" v-if="cruise">
                    <!-- Stepper -->
                    <BookingStepper :active-step="activeStep" class="mb-6" />

                    <header class="mb-5">
                        <h2 class="text-h4 font-weight-bold mb-1">Select your room type</h2>
                        <p class="text-body-2 text-grey-darken-1">
                            Pick the cabin and deal that works best for you.
                        </p>
                    </header>

                    <!-- Price per person FROM -->
                    <v-card elevation="3" rounded="lg" class="mb-6 price-card">
                        <!-- Tabs -->
                        <v-tabs v-model="selectedTab" color="primary" grow height="86" class="mb-2 price-tabs"
                            density="comfortable">
                            <v-tab v-for="type in roomTypes" :key="type" :value="type" class="py-4">
                                <div class="d-flex flex-column align-center text-center" style="line-height: 1.3;">
                                    <span class="font-weight-bold" :class="{ 'text-primary': selectedTab === type }">
                                        {{ type }}
                                    </span>

                                    <span class="font-weight-medium text-body-2 mt-1">
                                        <template v-if="minPriceByType[type] !== null">
                                            {{ currency(minPriceByType[type]) }} USD onwards
                                        </template>
                                        <template v-else>—</template>
                                    </span>

                                    <span class="text-caption text-grey-darken-1 mt-1">
                                        Includes taxes &amp; fees
                                    </span>
                                </div>
                            </v-tab>
                        </v-tabs>

                        <v-divider class="mb-4" />

                        <!-- Loading -->
                        <template v-if="loading">
                            <v-skeleton-loader v-for="i in 3" :key="i"
                                type="list-item-two-line, divider, list-item-two-line" class="mb-4" rounded="lg" />
                        </template>

                        <!-- Tab Content -->
                        <template v-else>
                            <template v-if="filteredListings.length">
                                <div v-for="(listing, idx) in filteredListings" :key="listing.id" class="cabin-option"
                                    :class="{ 'cabin-option--selected': isSelected(listing) }"
                                    @click="selectListing(listing)">
                                    <v-row class="pa-4" align="stretch" no-gutters>
                                        <!-- LEFT: Title + meta -->
                                        <v-col cols="12" md="5" class="pr-md-4">
                                            <div class="d-flex align-center justify-space-between mb-1">
                                                <v-chip v-if="listing.selected_code" size="small" label variant="tonal">
                                                    {{ listing.selected_code }}
                                                </v-chip>

                                                <v-chip v-if="isBestValue(listing)" color="success" label size="small"
                                                    class="ml-auto">
                                                    Best Value
                                                </v-chip>
                                            </div>

                                            <div class="text-h6 text-grey-darken-1 mt-2">
                                                {{ listing.description || 'Description coming soon.' }}
                                            </div>

                                            <div class="text-subtitle-1 text-grey-darken-1 mt-2">
                                                <div class="mb-1">
                                                    <v-icon size="16" class="mr-1">mdi-door</v-icon>
                                                    Cabin: <b>{{ listing.cabin_number || 'TBD' }}</b>
                                                </div>
                                                <div class="mb-1">
                                                    <v-icon size="16" class="mr-1">mdi-account-multiple</v-icon>
                                                    Sleeps: <b>{{ listing.guest_limit || 'TBD' }}</b>
                                                </div>
                                            </div>
                                        </v-col>

                                        <!-- MIDDLE: Perks -->
                                        <v-col cols="12" md="4" class="py-3 py-md-0 pr-md-4">
                                            <div class="text-h6 font-weight-medium mb-2">Bonus perks</div>
                                            <div class="d-flex flex-wrap">
                                                <v-chip v-for="perk in (listing.perks || [])" :key="perk" label
                                                    size="small" class="me-2 mb-2" color="primary" variant="tonal">
                                                    {{ formatPerk(perk) }}
                                                </v-chip>

                                                <div v-if="!listing.perks || !listing.perks.length"
                                                    class="text-grey-darken-1 text-subtitle-2">
                                                    — No extra perks
                                                </div>
                                            </div>
                                        </v-col>

                                        <!-- RIGHT: Price + selection indicator -->
                                        <v-col cols="12" md="3"
                                            class="d-flex flex-column justify-space-between align-end">
                                            <div class="select-indicator d-flex align-center mb-3">
                                                <v-icon size="20" :color="isSelected(listing) ? 'primary' : 'grey'">
                                                    {{ isSelected(listing) ? 'mdi-radiobox-marked' :
                                                        'mdi-radiobox-blank' }}
                                                </v-icon>
                                                <span class="text-caption ml-2"
                                                    :class="{ 'text-primary': isSelected(listing) }">
                                                    {{ isSelected(listing) ? 'Selected' : 'Select' }}
                                                </span>
                                            </div>

                                            <div class="text-right">
                                                <div class="price-lg">
                                                    {{ currency(listing.total_guest_price) }}
                                                </div>
                                                <div class="text-subtitle-2">
                                                    Deposit: <b>{{ currency(listing.deposit) }}</b>
                                                </div>
                                                <div class="text-subtitle-1 text-grey-darken-1">
                                                    Tips Included: <b>{{ listing.tips_included ?? 'N/A' }}</b>
                                                </div>
                                            </div>
                                        </v-col>
                                    </v-row>

                                    <!-- Divider between rows -->
                                    <v-divider v-if="idx < filteredListings.length - 1" />
                                </div>
                            </template>

                            <!-- Empty state -->
                            <v-sheet v-else
                                class="empty-state d-flex flex-column align-center justify-center text-center pa-8"
                                rounded="lg" elevation="0">
                                <v-icon size="48" color="primary" class="mb-3">mdi-bed-empty</v-icon>
                                <div class="text-h6 font-weight-bold mb-1">
                                    No {{ selectedTab }} listings available
                                </div>
                                <div class="text-subtitle-1 text-grey-darken-1 mb-4">
                                    Please try a different cabin category or check back later.
                                </div>
                                <v-btn color="primary" rounded="lg" variant="tonal" @click="pickFirstAvailableTab">
                                    View available cabins
                                </v-btn>
                            </v-sheet>
                        </template>
                    </v-card>

                    <!-- BACK + CONTINUE -->
                    <div class="d-flex justify-space-between align-center mt-4 bottom-actions">
                        <v-btn color="primary" variant="outlined" size="large" class="px-8"
                            prepend-icon="mdi-arrow-left" @click="goBack">
                            Back
                        </v-btn>

                        <v-btn color="primary" size="large" rounded="lg" class="px-8" :disabled="!selectedListing"
                            @click="continueWithSelection">
                            Continue
                        </v-btn>
                    </div>

                </section>
            </div>
        </v-container>
    </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import FullPageLoader from '@/components/FullPageLoader.vue'
import BookingStepper from '@/components/BookingStepper.vue'

export default {
    name: 'CabinPick',
    components: {
        FullPageLoader,
        BookingStepper,
    },
    data() {
        return {
            activeStep: 1,
            cruiseId: null,
            cruise: null,
            roomTypes: ['Inside', 'Outside', 'Balcony', 'Suite'],
            selectedTab: 'Inside',
            selectedListingId: null,
            listings: [],
            adults: 2,
            children: 0,
            hasSenior: false,
            country: null,
            state: null,
            draft: null,
            loading: true,
        }
    },
    async created() {
        this.cruiseId = this.$route.params.id

        this.draft = this.$route.state?.bookingDraft
            || JSON.parse(sessionStorage.getItem('bookingDraft') || 'null')

        if (!this.draft) {
            this.$router.replace({ name: 'DealDetail', params: { id: this.cruiseId } })
            return
        }

        await Promise.all([this.fetchCruise(), this.fetchListings()])
        this.pickFirstAvailableTab()
        this.loading = false
    },
    computed: {
        filteredListings() {
            return this.listings.filter(
                l => l.room_type?.toLowerCase() === this.selectedTab.toLowerCase()
            )
        },
        minPriceByType() {
            const map = {}
            for (const type of this.roomTypes) {
                const prices = this.listings
                    .filter(l => (l.room_type || '').toLowerCase() === type.toLowerCase())
                    .map(l => Number(l.total_guest_price))
                    .filter(v => Number.isFinite(v))
                map[type] = prices.length ? Math.min(...prices) : null
            }
            return map
        },
        selectedListing() {
            return this.listings.find(l => l.id === this.selectedListingId) || null
        },
        isStepValid() {
            return !!this.adults && this.adults >= 1
        },
        isLoading() {
            return this.cruise == null
        },
        minPrices() {
            const out = { Inside: null, Outside: null, Balcony: null, Suite: null }
            for (const l of this.listings) {
                const key = (l.room_type || '').trim()
                if (!out[key] || Number(l.total_cost) < Number(out[key])) {
                    out[key] = l.total_cost
                }
            }
            return out
        },
        hero() { return this.cruise?.full_image_url || this.cruise?.imgSrc || '' },
        title() { return this.cruise?.title || '—' },
        departFrom() { return this.cruise?.departure_port || '—' },
        shipName() { return this.cruise?.ship_name || '—' },
        numberOfNights() {
            const m = (this.cruise?.title || '').match(/(\d+)\s*days?/i)
            return m ? parseInt(m[1], 10) : null
        },
        startDateFmt() {
            const ymd = this.cruise?.date
            if (!ymd) return '—'
            const [y, m, d] = ymd.split('-').map(Number)
            const dt = new Date(y, m - 1, d)
            return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        },
        endDateFmt() {
            if (this.cruise?.end_date) {
                const [y, m, d] = this.cruise.end_date.split('-').map(Number)
                return new Date(y, m - 1, d).toLocaleDateString(
                    'en-US',
                    { month: 'short', day: 'numeric', year: 'numeric' }
                )
            }
            if (this.cruise?.date && this.numberOfNights) {
                const [y, m, d] = this.cruise.date.split('-').map(Number)
                const dt = new Date(y, m - 1, d)
                dt.setDate(dt.getDate() + this.numberOfNights)
                return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            }
            return '—'
        },
        guestsLabel() {
            const a = `${this.adults} Adult${this.adults > 1 ? 's' : ''}`
            const c = this.children ? `, ${this.children} Child${this.children > 1 ? 'ren' : ''}` : ''
            return a + c
        }
    },
    methods: {
        async fetchCruise() {
            const { data, error } = await supabase
                .from('cruises')
                .select('*')
                .eq('id', this.cruiseId)
                .single()
            if (!error) this.cruise = data
        },
        async fetchListings() {
            const { data, error } = await supabase
                .from('listings')
                .select()
                .eq('sailing_id', this.cruiseId)
            if (!error) this.listings = data || []
        },
        pickFirstAvailableTab() {
            for (const t of this.roomTypes) {
                const first = this.listings.find(
                    l => (l.room_type || '').toLowerCase() === t.toLowerCase()
                )
                if (first) {
                    this.selectedTab = t
                    this.selectedListingId = first.id
                    return
                }
            }
            this.selectedListingId = null
        },
        selectListing(listing) {
            this.selectedListingId = listing.id
        },
        isSelected(listing) {
            return listing.id === this.selectedListingId
        },
        currency(val) {
            if (val == null || val === '') return 'N/A'
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
            }).format(val)
        },
        formatPerk(perk) {
            const map = {
                onboardCredit50: 'Onboard Credit - $50',
                onboardCredit100: 'Onboard Credit - $100',
                other: 'Other',
                specialtyDining: 'Specialty Dining',
                prepaidGratuity: 'Prepaid Gratuity',
                beveragePackage: 'Beverage Package',
                internetPackage: 'Internet Package',
                shoreExcursionCredit: 'Shore Excursion Credit'
            }
            return map[perk] || perk
        },
        isBestValue(listing) {
            const sameTypeListings = this.listings.filter(
                l => (l.room_type || '').toLowerCase() === (listing.room_type || '').toLowerCase()
            )
            if (!sameTypeListings.length) return false
            const minPrice = Math.min(
                ...sameTypeListings
                    .map(l => Number(l.total_guest_price))
                    .filter(v => Number.isFinite(v))
            )
            return Number(listing.total_guest_price) === minPrice
        },
        continueWithSelection() {
            if (!this.selectedListing) return

            const previous = JSON.parse(sessionStorage.getItem('bookingDraft') || '{}')

            const step2Payload = {
                selectedListingId: this.selectedListing.id,
                selectedRoomType: this.selectedListing.room_type || this.selectedTab,
                cabinNumber: this.selectedListing.cabin_number,
                selectedCabinDeck: this.selectedListing.deck,
                selectedCabinPrice: this.selectedListing.total_guest_price,
                selectedCabinDeposit: this.selectedListing.deposit,
                selectedCabinPerks: this.selectedListing.perks || []
            }

            const updatedDraft = {
                cruiseId: previous.cruiseId ?? null,
                guestsMeta: previous.guestsMeta ?? null,
                residency: previous.residency ?? null,
                ...step2Payload
            }

            // persist for next steps
            sessionStorage.setItem('bookingDraft', JSON.stringify(updatedDraft))

             this.$router.push({
                name: 'Preferences',
                params: { id: this.cruiseId, listing: this.selectedListing.id }
            })
        },
        goBack() {
            // Keep the selected cabin in the draft when going back
            const updatedDraft = {
                ...this.draft,
            }

            sessionStorage.setItem('bookingDraft', JSON.stringify(updatedDraft))

            this.$router.push({
                name: 'DealDetail',        // make sure this matches your first-step route name
                params: { id: this.cruise.id }
            })
        }

    }
}
</script>

<style scoped>
/* MAIN CONTENT WRAP */
.content-inner {
    margin-top: -16px;
    /* slight overlap feeling */
}

/* 2-rail layout */
.two-rail {
    display: flex;
    align-items: flex-start;
    gap: 24px;
}

/* LEFT PANEL */
.left-panel {
    --sticky-offset: 96px;
    flex: 0 0 360px;
    max-width: 360px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
    position: sticky;
    top: var(--sticky-offset);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.left-hero {
    flex: 0 0 auto;
}

.left-content {
    padding: 18px 18px 20px;
}

.left-title {
    line-height: 1.3;
}

/* PRICE SECTION AT BOTTOM */
.pricing-section {
    border-top: 1px solid #eee;
    padding-top: 12px;
    margin-top: 16px;
    padding-bottom: 4px;
}

.pricing-header {
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #666;
}

.pricing-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;
}

.pricing-label {
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.65);
}

.pricing-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
}

.pricing-section--empty .pricing-row {
    justify-content: flex-start;
}

/* RIGHT RAIL */
.right-rail {
    flex: 1 1 auto;
    min-width: 0;
}

/* labels on left summary */
.label {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
}

.value {
    color: rgba(0, 0, 0, 0.87);
}

/* price card header */
.price-card {
    overflow: hidden;
}

.price-header {
    background: var(--color-primary, #2436c8);
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

/* steppers / prices */
.price-lg {
    font-size: 1.4rem;
    font-weight: 700;
}

/* CABIN ROWS */
.cabin-option {
    cursor: pointer;
    transition: box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
    border-left: 3px solid transparent;
}

.cabin-option:hover {
    background-color: #fafafa;
}

.cabin-option--selected {
    background-color: #f4f6ff;
    border-left-color: var(--color-primary, #2436c8);
    box-shadow: 0 0 0 1px rgba(36, 54, 200, 0.16);
}

.select-indicator {
    align-self: flex-end;
}

.bottom-actions {
    gap: 12px;
}

@media (max-width: 600px) {
    .bottom-actions {
        flex-direction: column-reverse;
        align-items: stretch !important;
    }

    .bottom-actions .v-btn {
        width: 100%;
        justify-content: center;
    }
}


/* MOBILE / TABLET */
@media (max-width: 959.98px) {
    .two-rail {
        flex-direction: column;
    }

    .left-panel {
        position: static;
        max-width: 100%;
    }

    .content-inner {
        padding-inline: 16px !important;
    }
}
</style>
